import React, { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import Select from 'react-select';
import { useDeviceInfo } from '../../DeviceInfo';
import { useDocumentData } from './hooks/useDocumentData';
import { deriveFilterOptions } from './utils/documentHelpers';
import DocumentFilters from './components/DocumentFilters';
import DocumentTable from './components/DocumentTable';
import './documents.scss';

const siteSelectStyles = {
    container: (base) => ({ ...base, flex: 1, minWidth: 0 }),
    control: (base) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '4px',
        boxShadow: 'none',
        flexWrap: 'nowrap',
        '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
    }),
    singleValue: (base) => ({ ...base, color: 'white', fontSize: '12px' }),
    placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', fontSize: '12px' }),
    input: (base) => ({ ...base, color: 'white', fontSize: '12px' }),
    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: '#1e163c', border: '1px solid rgba(255,255,255,0.12)', minWidth: '200px' }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        padding: '6px 10px',
        backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.1)' : 'transparent',
        color: 'white',
        '&:active': { backgroundColor: 'rgba(0,93,229,0.2)' }
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)' }),
    clearIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)' }),
};

const INITIAL_FILTERS = {
    category: null,
    site: null,
    search: '',
};

const Documents = forwardRef(({
    preloadedData,
    setPreloadedData,
    dataLoaded,
    setDataLoaded,
}, ref) => {
    const { deviceInfo } = useDeviceInfo();
    const isMobileView = isMobile || isTablet;

    const {
        documentData,
        loading,
        error,
        dataFetched,
        refresh,
    } = useDocumentData(deviceInfo, preloadedData);

    const [filters, setFilters] = useState(INITIAL_FILTERS);

    const filterOptions = useMemo(() => deriveFilterOptions(documentData), [documentData]);

    const filteredDocuments = useMemo(() => {
        let result = documentData;

        if (filters.category) result = result.filter((d) => d.category === filters.category.value);
        if (filters.site) result = result.filter((d) => d.siteName === filters.site.value);

        if (filters.search) {
            const term = filters.search.toLowerCase();
            result = result.filter((d) =>
                (d.title && d.title.toLowerCase().includes(term)) ||
                (d.category && d.category.toLowerCase().includes(term)) ||
                (d.siteName && d.siteName.toLowerCase().includes(term)) ||
                (d.clientName && d.clientName.toLowerCase().includes(term))
            );
        }

        return result;
    }, [documentData, filters]);

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
    }, []);

    // Sync data back to parent
    useEffect(() => {
        if (dataFetched && !dataLoaded && documentData.length > 0 && setPreloadedData && setDataLoaded) {
            setPreloadedData({ documentData });
            setDataLoaded(true);
        }
    }, [dataFetched, dataLoaded, documentData, setPreloadedData, setDataLoaded]);

    // Expose renderFilters for mobile sidebar
    useImperativeHandle(ref, () => ({
        renderFilters: () => (
            <div className="sidebar-filter-section">
                <div className="sidebar-filter-item">
                    <label>Category</label>
                    <Select
                        value={filters.category}
                        onChange={(val) => handleFilterChange('category', val)}
                        options={filterOptions.categories}
                        placeholder="All Categories"
                        isClearable
                        isSearchable
                        styles={siteSelectStyles}
                    />
                </div>
                <div className="sidebar-filter-item">
                    <label>Site</label>
                    <Select
                        value={filters.site}
                        onChange={(val) => handleFilterChange('site', val)}
                        options={filterOptions.sites}
                        placeholder="All Sites"
                        isClearable
                        isSearchable
                        styles={siteSelectStyles}
                    />
                </div>
            </div>
        ),
    }), [filters, filterOptions, handleFilterChange]);

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="small-spinner">
                    <span></span><span></span><span></span><span></span>
                </div>
                <p className="mt-3" style={{ color: 'white' }}>Loading document data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>Error loading data: {error}</p>
                <button className="documents__retry-btn" onClick={refresh}>Retry</button>
            </div>
        );
    }

    if (!documentData || documentData.length === 0) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>No document data available.</p>
            </div>
        );
    }

    return (
        <div className="documents">
            <div className="documents__panel">
                {!isMobileView && (
                    <DocumentFilters
                        filterOptions={filterOptions}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                    />
                )}
                <DocumentTable documents={filteredDocuments} />
            </div>
        </div>
    );
});

export default Documents;
